import React, { useState, useEffect, useMemo } from "react";
import { FileSpreadsheet, Printer, ArrowLeft, Columns } from "lucide-react";
import { formatDMY } from "@/lib/dmy";

const RAW_COL_DEFS = [
  { key: "client", label: "Client" },
  { key: "month", label: "Month" },
  { key: "parcels", label: "Parcels" },
  { key: "costing", label: "Costing" },
  { key: "sales", label: "Sales" },
  { key: "raw_funds", label: "Raw Funds" },
  { key: "sale_minus_raw", label: "Sale − Raw Funds" },
  { key: "cost_minus_raw", label: "Cost − Raw Funds" },
];

const rfFmt = (v) => {
  if (!v && v !== 0) return "-";
  return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(v);
};

const RawFundsPrintView = ({ data }) => {
  const [cols, setCols] = useState(data.columns);
  const [showColPicker, setShowColPicker] = useState(false);

  const totals = useMemo(() => ({
    parcels: data.records.reduce((s, r) => s + (r.parcels || 0), 0),
    costing: data.records.reduce((s, r) => s + (r.costing || 0), 0),
    sales: data.records.reduce((s, r) => s + (r.sales || 0), 0),
    raw_funds: data.records.reduce((s, r) => s + (r.raw_funds || 0), 0),
    sale_minus_raw: data.records.reduce((s, r) => s + (r.sale_minus_raw || 0), 0),
    cost_minus_raw: data.records.reduce((s, r) => s + (r.cost_minus_raw || 0), 0),
  }), [data]);

  const handleExcelExport = () => {
    const headers = [];
    if (cols.client) headers.push("Client", "Type");
    RAW_COL_DEFS.slice(1).forEach(({ key, label }) => { if (cols[key]) headers.push(label); });
    const rows = data.records.map((r) => {
      const row = [];
      if (cols.client) { row.push(r.client_code); row.push(r.client_type); }
      if (cols.month) row.push(r.month_name);
      if (cols.parcels) row.push(r.parcels || 0);
      if (cols.costing) row.push(r.costing || 0);
      if (cols.sales) row.push(r.sales || 0);
      if (cols.raw_funds) row.push(r.raw_funds || 0);
      if (cols.sale_minus_raw) row.push(r.sale_minus_raw || 0);
      if (cols.cost_minus_raw) row.push(r.cost_minus_raw || 0);
      return row;
    });
    let xml = '<?xml version="1.0" encoding="UTF-8"?>\n<?mso-application progid="Excel.Sheet"?>\n';
    xml += '<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet" xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">\n';
    xml += '<Styles><Style ss:ID="header"><Font ss:Bold="1" ss:Size="11"/><Interior ss:Color="#E0F7FA" ss:Pattern="Solid"/></Style>';
    xml += '<Style ss:ID="num"><NumberFormat ss:Format="#,##0.00"/></Style></Styles>\n';
    xml += '<Worksheet ss:Name="Raw Funds">\n<Table>\n<Row>';
    headers.forEach((h) => { xml += `<Cell ss:StyleID="header"><Data ss:Type="String">${h}</Data></Cell>`; });
    xml += '</Row>\n';
    rows.forEach((row) => {
      xml += '<Row>';
      row.forEach((val) => {
        xml += typeof val === "number"
          ? `<Cell ss:StyleID="num"><Data ss:Type="Number">${val}</Data></Cell>`
          : `<Cell><Data ss:Type="String">${String(val).replace(/&/g, "&amp;").replace(/</g, "&lt;")}</Data></Cell>`;
      });
      xml += '</Row>\n';
    });
    xml += '</Table>\n</Worksheet>\n</Workbook>';
    const blob = new Blob([xml], { type: "application/vnd.ms-excel" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `raw_funds_overview_${data.year}.xls`;
    a.click();
  };

  return (
    <div className="bg-white min-h-screen">
      <div className="print:hidden sticky top-0 z-50 bg-white border-b shadow-sm px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <button onClick={() => window.close()} className="text-slate-500 hover:text-slate-700 flex items-center gap-1 text-sm">
            <ArrowLeft className="w-4 h-4" /> Close
          </button>
          <div className="h-5 w-px bg-slate-200" />
          <h1 className="text-sm font-semibold text-slate-800">{data.title}</h1>
          <span className="text-xs text-slate-500">({data.partners})</span>
          <span className="text-xs text-slate-400">{data.records.length} records</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <button
              onClick={() => setShowColPicker(!showColPicker)}
              className="flex items-center gap-1 px-3 py-1.5 border rounded-md text-sm hover:bg-slate-50 text-slate-700"
              data-testid="column-picker-btn"
            >
              <Columns className="w-4 h-4" /> Columns
            </button>
            {showColPicker && (
              <div className="absolute right-0 top-full mt-1 bg-white border rounded-lg shadow-lg p-3 z-50 w-52">
                {RAW_COL_DEFS.map(({ key, label }) => (
                  <label key={key} className="flex items-center gap-2 py-0.5 cursor-pointer hover:bg-slate-50 rounded px-1">
                    <input type="checkbox" checked={cols[key]} onChange={() => setCols((p) => ({ ...p, [key]: !p[key] }))} className="rounded" />
                    <span className="text-xs">{label}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
          <button onClick={handleExcelExport} className="flex items-center gap-1 px-3 py-1.5 bg-green-600 text-white rounded-md text-sm hover:bg-green-700" data-testid="excel-export-btn">
            <FileSpreadsheet className="w-4 h-4" /> Excel
          </button>
          <button onClick={() => window.print()} className="flex items-center gap-1 px-3 py-1.5 bg-slate-800 text-white rounded-md text-sm hover:bg-slate-900" data-testid="print-page-btn">
            <Printer className="w-4 h-4" /> Print
          </button>
        </div>
      </div>

      <div className="hidden print:block px-6 pt-6 pb-2">
        <h1 className="text-lg font-bold">{data.title}</h1>
        <p className="text-xs text-slate-500">Partners: {data.partners} | Records: {data.records.length} | Generated: {formatDMY(new Date())}</p>
      </div>

      <div className="px-4 py-4 print:px-2 print:py-0">
        <table className="w-full border-collapse text-xs" data-testid="raw-print-table">
          <thead>
            <tr className="bg-slate-100 print:bg-slate-200">
              {cols.client && <th className="border border-slate-300 px-2 py-1.5 text-left font-semibold">Client</th>}
              {cols.month && <th className="border border-slate-300 px-2 py-1.5 text-left font-semibold">Month</th>}
              {cols.parcels && <th className="border border-slate-300 px-2 py-1.5 text-right font-semibold">Parcels</th>}
              {cols.costing && <th className="border border-slate-300 px-2 py-1.5 text-right font-semibold">Costing</th>}
              {cols.sales && <th className="border border-slate-300 px-2 py-1.5 text-right font-semibold">Sales</th>}
              {cols.raw_funds && <th className="border border-slate-300 px-2 py-1.5 text-right font-semibold bg-cyan-50">Raw Funds</th>}
              {cols.sale_minus_raw && <th className="border border-slate-300 px-2 py-1.5 text-right font-semibold bg-emerald-50">Sale − Raw Funds</th>}
              {cols.cost_minus_raw && <th className="border border-slate-300 px-2 py-1.5 text-right font-semibold bg-amber-50">Cost − Raw Funds</th>}
            </tr>
          </thead>
          <tbody>
            {data.records.map((r, idx) => (
              <tr key={idx} className={idx % 2 === 0 ? "bg-white" : "bg-slate-50/50"}>
                {cols.client && (
                  <td className="border border-slate-200 px-2 py-1 font-medium">
                    {r.client_code} <span className="text-[9px] text-slate-400">{r.client_type}</span>
                  </td>
                )}
                {cols.month && <td className="border border-slate-200 px-2 py-1">{r.month_name}</td>}
                {cols.parcels && <td className="border border-slate-200 px-2 py-1 text-right">{r.parcels || "-"}</td>}
                {cols.costing && <td className="border border-slate-200 px-2 py-1 text-right">{r.costing ? rfFmt(r.costing) : "-"}</td>}
                {cols.sales && <td className="border border-slate-200 px-2 py-1 text-right">{r.sales ? rfFmt(r.sales) : "-"}</td>}
                {cols.raw_funds && <td className="border border-slate-200 px-2 py-1 text-right font-semibold text-cyan-700">{r.raw_funds ? rfFmt(r.raw_funds) : "-"}</td>}
                {cols.sale_minus_raw && (
                  <td className={`border border-slate-200 px-2 py-1 text-right font-medium ${r.sale_minus_raw >= 0 ? "text-emerald-700" : "text-red-600"}`}>
                    {r.sales || r.raw_funds ? rfFmt(r.sale_minus_raw) : "-"}
                  </td>
                )}
                {cols.cost_minus_raw && (
                  <td className={`border border-slate-200 px-2 py-1 text-right font-medium ${r.cost_minus_raw >= 0 ? "text-amber-700" : "text-red-600"}`}>
                    {r.costing || r.raw_funds ? rfFmt(r.cost_minus_raw) : "-"}
                  </td>
                )}
              </tr>
            ))}
            <tr className="bg-slate-200 font-bold">
              {cols.client && <td className="border border-slate-300 px-2 py-1.5 font-bold">TOTAL</td>}
              {cols.month && <td className="border border-slate-300 px-2 py-1.5"></td>}
              {cols.parcels && <td className="border border-slate-300 px-2 py-1.5 text-right">{totals.parcels}</td>}
              {cols.costing && <td className="border border-slate-300 px-2 py-1.5 text-right">{rfFmt(totals.costing)}</td>}
              {cols.sales && <td className="border border-slate-300 px-2 py-1.5 text-right">{rfFmt(totals.sales)}</td>}
              {cols.raw_funds && <td className="border border-slate-300 px-2 py-1.5 text-right text-cyan-800">{rfFmt(totals.raw_funds)}</td>}
              {cols.sale_minus_raw && <td className="border border-slate-300 px-2 py-1.5 text-right text-emerald-800">{rfFmt(totals.sale_minus_raw)}</td>}
              {cols.cost_minus_raw && <td className="border border-slate-300 px-2 py-1.5 text-right text-amber-800">{rfFmt(totals.cost_minus_raw)}</td>}
            </tr>
          </tbody>
        </table>
      </div>

      <style>{`
        @media print {
          body { margin: 0; padding: 0; }
          @page { size: landscape; margin: 10mm; }
          table { font-size: 9px !important; }
          th, td { padding: 2px 4px !important; }
        }
      `}</style>
    </div>
  );
};

const FinancialOverviewPrintPage = () => {
  const [data, setData] = useState(null);
  const [visibleColumns, setVisibleColumns] = useState(null);

  useEffect(() => {
    const raw = localStorage.getItem("fo_print_data");
    if (raw) {
      try {
        const parsed = JSON.parse(raw);
        setData(parsed);
        setVisibleColumns(parsed.columns);
      } catch {
        setData(null);
      }
    }
  }, []);

  const fmt = (v) => {
    if (!v && v !== 0) return "-";
    return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(v);
  };

  // Compute totals
  const totals = useMemo(() => {
    if (!data) return {};
    const recs = data.records || [];
    return {
      parcels: recs.reduce((s, r) => s + (r.parcels || 0), 0),
      costing: recs.reduce((s, r) => s + (r.costing || 0), 0),
      sales: recs.reduce((s, r) => s + (r.sales || 0), 0),
      total_gp: recs.reduce((s, r) => s + (r.total_gp || 0), 0),
      verified_payments: recs.reduce((s, r) => s + (r.verified_payments || 0), 0),
      credits: recs.reduce((s, r) => s + (r.credits || 0), 0),
      debits: recs.reduce((s, r) => s + (r.debits || 0), 0),
      partner_payment: recs.reduce((s, r) => s + (r.partner_payment || 0), 0),
    };
  }, [data]);

  const handlePrint = () => window.print();

  const handleExcelExport = () => {
    if (!data) return;
    const cols = visibleColumns || data.columns;
    const gpCols = data.gpColumns || [];

    // Build headers
    const headers = [];
    if (cols.client) headers.push("Client", "Type");
    if (cols.month) headers.push("Month");
    if (cols.parcels) headers.push("Parcels");
    if (cols.costing) headers.push("Costing");
    if (cols.sales) headers.push("Sales");
    if (cols.total_gp) headers.push("Total GP");
    if (cols.gp_columns) gpCols.forEach((g) => headers.push(`GP ${g.name} (${g.percentage}%)`));
    if (cols.payments) headers.push("Payments");
    if (cols.credits) headers.push("Credits");
    if (cols.debits) headers.push("Debits");
    if (cols.partner_payment) headers.push("Partner Payment");
    if (cols.net_position) gpCols.filter((g) => !g.is_self).forEach((g) => headers.push(`Net ${g.name}`));
    if (cols.status) headers.push("Status");
    if (cols.narration) headers.push("Narration");

    // Build rows
    const rows = data.records.map((r) => {
      const row = [];
      const dist = r.gp_distribution || [];
      if (cols.client) { row.push(r.client_code); row.push(r.client_type); }
      if (cols.month) row.push(r.month_name);
      if (cols.parcels) row.push(r.parcels || 0);
      if (cols.costing) row.push(r.costing || 0);
      if (cols.sales) row.push(r.sales || 0);
      if (cols.total_gp) row.push(r.total_gp || 0);
      if (cols.gp_columns) gpCols.forEach((_, i) => row.push(dist[i]?.amount || 0));
      if (cols.payments) row.push(r.verified_payments || 0);
      if (cols.credits) row.push(r.credits || 0);
      if (cols.debits) row.push(r.debits || 0);
      if (cols.partner_payment) row.push(r.partner_payment || 0);
      if (cols.net_position) gpCols.filter((g) => !g.is_self).forEach((_, i) => {
        const pIdx = gpCols.findIndex((g, gi) => !g.is_self && gpCols.slice(0, gi).filter((x) => !x.is_self).length === i);
        row.push(dist[pIdx]?.net_position || 0);
      });
      if (cols.status) row.push(r.settlement_status || "pending");
      if (cols.narration) row.push(r.narration || "");
      return row;
    });

    // Build Excel XML (simple SpreadsheetML format for .xls)
    let xml = '<?xml version="1.0" encoding="UTF-8"?>\n';
    xml += '<?mso-application progid="Excel.Sheet"?>\n';
    xml += '<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet" xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">\n';
    xml += '<Styles><Style ss:ID="header"><Font ss:Bold="1" ss:Size="11"/><Interior ss:Color="#E8F5E9" ss:Pattern="Solid"/></Style>';
    xml += '<Style ss:ID="total"><Font ss:Bold="1" ss:Size="11"/><Interior ss:Color="#F5F5F5" ss:Pattern="Solid"/></Style>';
    xml += '<Style ss:ID="num"><NumberFormat ss:Format="#,##0.00"/></Style></Styles>\n';
    xml += `<Worksheet ss:Name="Financial Overview">\n<Table>\n`;
    // Header row
    xml += '<Row>';
    headers.forEach((h) => { xml += `<Cell ss:StyleID="header"><Data ss:Type="String">${h}</Data></Cell>`; });
    xml += '</Row>\n';
    // Data rows
    rows.forEach((row) => {
      xml += '<Row>';
      row.forEach((val) => {
        if (typeof val === "number") {
          xml += `<Cell ss:StyleID="num"><Data ss:Type="Number">${val}</Data></Cell>`;
        } else {
          xml += `<Cell><Data ss:Type="String">${String(val).replace(/&/g, "&amp;").replace(/</g, "&lt;")}</Data></Cell>`;
        }
      });
      xml += '</Row>\n';
    });
    xml += '</Table>\n</Worksheet>\n</Workbook>';

    const blob = new Blob([xml], { type: "application/vnd.ms-excel" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `financial_overview_${data.year}.xls`;
    a.click();
  };

  const toggleColumn = (key) => {
    setVisibleColumns((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const [showColPicker, setShowColPicker] = useState(false);

  if (!data) {
    return (
      <div className="flex items-center justify-center h-screen bg-white">
        <div className="text-center">
          <p className="text-lg text-slate-600">No print data found.</p>
          <p className="text-sm text-slate-400 mt-1">Please use the Print button from the Financial Overview page.</p>
        </div>
      </div>
    );
  }

  const cols = visibleColumns || data.columns;
  const gpCols = data.gpColumns || [];

  if (data.mode === "raw_funds") {
    return <RawFundsPrintView data={data} />;
  }

  return (
    <div className="bg-white min-h-screen">
      {/* Toolbar - hidden when printing */}
      <div className="print:hidden sticky top-0 z-50 bg-white border-b shadow-sm px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <button onClick={() => window.close()} className="text-slate-500 hover:text-slate-700 flex items-center gap-1 text-sm">
            <ArrowLeft className="w-4 h-4" /> Close
          </button>
          <div className="h-5 w-px bg-slate-200" />
          <h1 className="text-sm font-semibold text-slate-800">{data.title}</h1>
          <span className="text-xs text-slate-500">({data.partners})</span>
          <span className="text-xs text-slate-400">{data.records.length} records</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <button
              onClick={() => setShowColPicker(!showColPicker)}
              className="flex items-center gap-1 px-3 py-1.5 border rounded-md text-sm hover:bg-slate-50 text-slate-700"
              data-testid="column-picker-btn"
            >
              <Columns className="w-4 h-4" /> Columns
            </button>
            {showColPicker && (
              <div className="absolute right-0 top-full mt-1 bg-white border rounded-lg shadow-lg p-3 z-50 w-52">
                {[
                  { key: "client", label: "Client" },
                  { key: "month", label: "Month" },
                  { key: "parcels", label: "Parcels" },
                  { key: "costing", label: "Costing" },
                  { key: "sales", label: "Sales" },
                  { key: "total_gp", label: "Total GP" },
                  { key: "gp_columns", label: "GP Distribution" },
                  { key: "payments", label: "Payments" },
                  { key: "credits", label: "Credits" },
                  { key: "debits", label: "Debits" },
                  { key: "partner_payment", label: "Partner Pmt" },
                  { key: "net_position", label: "Net Position" },
                  { key: "status", label: "Status" },
                  { key: "narration", label: "Narration" },
                ].map(({ key, label }) => (
                  <label key={key} className="flex items-center gap-2 py-0.5 cursor-pointer hover:bg-slate-50 rounded px-1">
                    <input type="checkbox" checked={cols[key]} onChange={() => toggleColumn(key)} className="rounded" />
                    <span className="text-xs">{label}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
          <button
            onClick={handleExcelExport}
            className="flex items-center gap-1 px-3 py-1.5 bg-green-600 text-white rounded-md text-sm hover:bg-green-700"
            data-testid="excel-export-btn"
          >
            <FileSpreadsheet className="w-4 h-4" /> Excel
          </button>
          <button
            onClick={handlePrint}
            className="flex items-center gap-1 px-3 py-1.5 bg-slate-800 text-white rounded-md text-sm hover:bg-slate-900"
            data-testid="print-page-btn"
          >
            <Printer className="w-4 h-4" /> Print
          </button>
        </div>
      </div>

      {/* Print Header - visible only when printing */}
      <div className="hidden print:block px-6 pt-6 pb-2">
        <h1 className="text-lg font-bold">{data.title}</h1>
        <p className="text-xs text-slate-500">Partners: {data.partners} | Records: {data.records.length} | Generated: {formatDMY(new Date())}</p>
      </div>

      {/* Table */}
      <div className="px-4 py-4 print:px-2 print:py-0">
        <table className="w-full border-collapse text-xs" data-testid="print-table">
          <thead>
            <tr className="bg-slate-100 print:bg-slate-200">
              {cols.client && <th className="border border-slate-300 px-2 py-1.5 text-left font-semibold">Client</th>}
              {cols.month && <th className="border border-slate-300 px-2 py-1.5 text-left font-semibold">Month</th>}
              {cols.parcels && <th className="border border-slate-300 px-2 py-1.5 text-right font-semibold">Parcels</th>}
              {cols.costing && <th className="border border-slate-300 px-2 py-1.5 text-right font-semibold">Costing</th>}
              {cols.sales && <th className="border border-slate-300 px-2 py-1.5 text-right font-semibold">Sales</th>}
              {cols.total_gp && <th className="border border-slate-300 px-2 py-1.5 text-right font-semibold bg-green-50">Total GP</th>}
              {cols.gp_columns && gpCols.map((g, i) => (
                <th key={`gp-${i}`} className={`border border-slate-300 px-2 py-1.5 text-right font-semibold ${g.is_self ? "bg-emerald-50" : "bg-violet-50"}`}>
                  GP {g.name}<br /><span className="font-normal text-[9px]">({g.percentage}%)</span>
                </th>
              ))}
              {cols.payments && <th className="border border-slate-300 px-2 py-1.5 text-right font-semibold bg-purple-50">Payments</th>}
              {cols.credits && <th className="border border-slate-300 px-2 py-1.5 text-right font-semibold bg-teal-50">Credit</th>}
              {cols.debits && <th className="border border-slate-300 px-2 py-1.5 text-right font-semibold bg-rose-50">Debit</th>}
              {cols.partner_payment && <th className="border border-slate-300 px-2 py-1.5 text-right font-semibold bg-orange-50">Partner Pmt</th>}
              {cols.net_position && gpCols.filter((g) => !g.is_self).map((g, i) => (
                <th key={`net-${i}`} className="border border-slate-300 px-2 py-1.5 text-right font-semibold bg-indigo-50">Net {g.name}</th>
              ))}
              {cols.status && <th className="border border-slate-300 px-2 py-1.5 text-center font-semibold">Status</th>}
              {cols.narration && <th className="border border-slate-300 px-2 py-1.5 text-left font-semibold max-w-[150px]">Narration</th>}
            </tr>
          </thead>
          <tbody>
            {data.records.map((r, idx) => {
              const dist = r.gp_distribution || [];
              return (
                <tr key={idx} className={idx % 2 === 0 ? "bg-white" : "bg-slate-50/50"}>
                  {cols.client && (
                    <td className="border border-slate-200 px-2 py-1 font-medium">
                      {r.client_code} <span className="text-[9px] text-slate-400">{r.client_type}</span>
                    </td>
                  )}
                  {cols.month && <td className="border border-slate-200 px-2 py-1">{r.month_name}</td>}
                  {cols.parcels && <td className="border border-slate-200 px-2 py-1 text-right">{r.parcels || "-"}</td>}
                  {cols.costing && <td className="border border-slate-200 px-2 py-1 text-right">{r.costing ? fmt(r.costing) : "-"}</td>}
                  {cols.sales && <td className="border border-slate-200 px-2 py-1 text-right">{r.sales ? fmt(r.sales) : "-"}</td>}
                  {cols.total_gp && (
                    <td className={`border border-slate-200 px-2 py-1 text-right font-semibold ${r.total_gp >= 0 ? "text-green-700" : "text-red-600"}`}>
                      {r.total_gp ? fmt(r.total_gp) : "-"}
                    </td>
                  )}
                  {cols.gp_columns && gpCols.map((g, i) => (
                    <td key={`gp-${i}`} className="border border-slate-200 px-2 py-1 text-right">
                      {dist[i]?.amount ? fmt(dist[i].amount) : "-"}
                    </td>
                  ))}
                  {cols.payments && <td className="border border-slate-200 px-2 py-1 text-right font-medium text-purple-700">{r.verified_payments ? fmt(r.verified_payments) : "-"}</td>}
                  {cols.credits && <td className="border border-slate-200 px-2 py-1 text-right text-teal-700">{r.credits ? fmt(r.credits) : "-"}</td>}
                  {cols.debits && <td className="border border-slate-200 px-2 py-1 text-right text-rose-700">{r.debits ? fmt(r.debits) : "-"}</td>}
                  {cols.partner_payment && <td className="border border-slate-200 px-2 py-1 text-right text-orange-700">{r.partner_payment ? fmt(r.partner_payment) : "-"}</td>}
                  {cols.net_position && gpCols.filter((g) => !g.is_self).map((g, i) => {
                    const pIdx = gpCols.findIndex((gp, gi) => !gp.is_self && gpCols.slice(0, gi).filter((x) => !x.is_self).length === i);
                    const d = dist[pIdx];
                    return (
                      <td key={`net-${i}`} className={`border border-slate-200 px-2 py-1 text-right font-semibold ${d?.net_position >= 0 ? "text-green-700" : "text-red-600"}`}>
                        {d?.net_position != null ? fmt(d.net_position) : "-"}
                      </td>
                    );
                  })}
                  {cols.status && (
                    <td className="border border-slate-200 px-2 py-1 text-center">
                      <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${
                        r.settlement_status === "complete" ? "bg-green-100 text-green-700" :
                        r.settlement_status === "partial" ? "bg-blue-100 text-blue-700" :
                        r.settlement_status === "disputed" ? "bg-red-100 text-red-700" :
                        "bg-amber-100 text-amber-700"
                      }`}>{r.settlement_status || "pending"}</span>
                    </td>
                  )}
                  {cols.narration && <td className="border border-slate-200 px-2 py-1 max-w-[150px] truncate text-slate-600">{r.narration || "-"}</td>}
                </tr>
              );
            })}
            {/* Totals Row */}
            <tr className="bg-slate-200 font-bold">
              {cols.client && <td className="border border-slate-300 px-2 py-1.5 font-bold">TOTAL</td>}
              {cols.month && <td className="border border-slate-300 px-2 py-1.5"></td>}
              {cols.parcels && <td className="border border-slate-300 px-2 py-1.5 text-right">{totals.parcels}</td>}
              {cols.costing && <td className="border border-slate-300 px-2 py-1.5 text-right">{fmt(totals.costing)}</td>}
              {cols.sales && <td className="border border-slate-300 px-2 py-1.5 text-right">{fmt(totals.sales)}</td>}
              {cols.total_gp && <td className="border border-slate-300 px-2 py-1.5 text-right text-green-800">{fmt(totals.total_gp)}</td>}
              {cols.gp_columns && gpCols.map((_, i) => <td key={`gpt-${i}`} className="border border-slate-300 px-2 py-1.5 text-right">-</td>)}
              {cols.payments && <td className="border border-slate-300 px-2 py-1.5 text-right text-purple-800">{fmt(totals.verified_payments)}</td>}
              {cols.credits && <td className="border border-slate-300 px-2 py-1.5 text-right text-teal-800">{fmt(totals.credits)}</td>}
              {cols.debits && <td className="border border-slate-300 px-2 py-1.5 text-right text-rose-800">{fmt(totals.debits)}</td>}
              {cols.partner_payment && <td className="border border-slate-300 px-2 py-1.5 text-right text-orange-800">{fmt(totals.partner_payment)}</td>}
              {cols.net_position && gpCols.filter((g) => !g.is_self).map((_, i) => <td key={`nett-${i}`} className="border border-slate-300 px-2 py-1.5 text-right">-</td>)}
              {cols.status && <td className="border border-slate-300 px-2 py-1.5"></td>}
              {cols.narration && <td className="border border-slate-300 px-2 py-1.5"></td>}
            </tr>
          </tbody>
        </table>
      </div>

      {/* Print styles */}
      <style>{`
        @media print {
          body { margin: 0; padding: 0; }
          @page { size: landscape; margin: 10mm; }
          table { font-size: 9px !important; }
          th, td { padding: 2px 4px !important; }
        }
      `}</style>
    </div>
  );
};

export default FinancialOverviewPrintPage;
